import React from 'react';
import { Theme } from '@app/theme';
import { createUseStyles, useTheme } from 'react-jss';
import { Outlet } from 'react-router-dom';

import { AnalyticsCategoryProvider, Categories } from '@app/analytics';
import {
  MainDashboardPage,
  PageTitle,
  SideBarItem,
  SideMenuPage,
} from 'components';

import { useSelector } from 'react-redux';
import { currentAccountRolesSelector } from 'store';
import { useTranslation } from 'react-i18next';
import { useIsAdmin } from 'queries';
import { useMe } from '@app/api';

const useStyles = createUseStyles((theme: Theme) => ({
  version: {
    ...theme.fonts.caption,
    textAlign: 'center',
    color: theme.colors.inkTertiary,
  },
  menuTitle: {
    ...theme.fonts.h3,
  },
  sectionTitleContainer: {
    paddingTop: theme.marginBase * 6,
    paddingBottom: theme.marginBase * 3,
    [theme.media.tablet]: {
      display: 'none',
    },
  },
  sectionTitle: {
    ...theme.fonts.h4,
  },
}));

export const Settings = () => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const { data: me } = useMe();
  const isAdmin = useIsAdmin();
  const { t } = useTranslation();

  return (
    <AnalyticsCategoryProvider category={Categories.SETTINGS}>
      <MainDashboardPage>
        <PageTitle title={t('settings.metaTitle')} />
        <SideMenuPage
          navTitle={t('settings.title')}
          navTitleClassName={classes.menuTitle}
          menuItems={[
            <div className={classes.sectionTitleContainer}>
              <span className={classes.sectionTitle}>
                {t('settings.accountSectionTitle')}
              </span>
            </div>,
            <SideBarItem
              to="account"
              text={t('settings.account.title')}
              icon="ri-user-3-line"
              variant="grayish"
            />,
            me?.isSsoAccount ? null : (
              <SideBarItem
                to="change-password"
                text={t('settings.changePassword.title')}
                icon="ri-lock-2-line"
                variant="grayish"
              />
            ),
            <SideBarItem
              to="tool-integrations"
              text={t('settings.toolIntegrations.title')}
              icon="ri-link-m"
              variant="grayish"
            />,
            <div className={classes.sectionTitleContainer}>
              <span className={classes.sectionTitle}>
                {t('settings.organisationSectionTitle')}
              </span>
            </div>,
            <SideBarItem
              to="team"
              text={t('teamInformations.title')}
              icon="ri-building-line"
              variant="grayish"
            />,
            <SideBarItem
              to="billing"
              text={t('billing.settings.title')}
              icon="ri-file-list-3-line"
              variant="grayish"
            />,
            <SideBarItem
              to="panels"
              text={t('settings.panels.title')}
              icon="ri-group-line"
              variant="grayish"
            />,
            <SideBarItem
              to="referrals"
              text={t('referrals.title')}
              icon="ri-hand-coin-line"
              variant="grayish"
            />,
            isAdmin && (
              <>
                <div className={classes.sectionTitleContainer}>
                  <span className={classes.sectionTitle}>
                    {t('settings.adminTitle')}
                  </span>
                </div>
                <SideBarItem
                  to="ui-kit"
                  text={t('settings.uikit.title')}
                  icon="ri-sticky-note-line"
                  variant="grayish"
                />
              </>
            ),
          ]}
          footer={
            <div className={classes.version}>{process.env.NX_APP_VERSION}</div>
          }
        >
          <Outlet />
        </SideMenuPage>
      </MainDashboardPage>
    </AnalyticsCategoryProvider>
  );
};
